import { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import logo from "@/assets/logo.png";
import { ayurvedicTreatments, otherTreatments } from "@/data/treatments";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isTreatmentsOpen, setIsTreatmentsOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Sulla home scrolla alla sezione; altrove naviga alla home + ancora.
  const scrollToSection = (sectionId: string) => {
    setIsOpen(false);
    if (isHome) {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = `/#${sectionId}`;
    }
  };

  const ayurvedici = ayurvedicTreatments;
  const altriTrattamenti = otherTreatments;
  // menu mobile: primi sei trattamenti (5 ayurvedici + 1 altro) come nel footer
  const mobileQuickLinks = [...ayurvedicTreatments.slice(0, 5), ...otherTreatments.slice(0, 1)];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background/85 backdrop-blur-md border-b ${
        isScrolled ? "border-border shadow-sm" : "border-transparent"
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center">
            <img
              src={logo}
              alt="ORIGINI Logo"
              width={353}
              height={200}
              className="h-12 md:h-16 w-auto"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection("home")}
              className="text-foreground hover:text-primary transition-colors font-medium"
            >
              Home
            </button>
            <button
              onClick={() => scrollToSection("studio")}
              className="text-foreground hover:text-primary transition-colors font-medium"
            >
              Lo Studio
            </button>
            
            {/* Dropdown Trattamenti */}
            <div
              className="relative"
              onMouseEnter={() => setIsTreatmentsOpen(true)}
              onMouseLeave={() => setIsTreatmentsOpen(false)}
            >
              <Link
                to="/trattamenti"
                className="flex items-center text-foreground hover:text-primary transition-colors font-medium"
              >
                Trattamenti
                <ChevronDown className="ml-1 w-4 h-4" />
              </Link>

              {isTreatmentsOpen && (
                // pt-2 (padding, non margin) crea lo spazio visivo SENZA un buco
                // non-hoverabile tra pulsante e pannello: la tendina resta cliccabile.
                <div className="absolute top-full left-0 pt-2 w-64 z-50">
                  <div className="bg-card border border-border rounded-lg shadow-xl py-2 max-h-[70vh] overflow-y-auto">
                    <Link
                      to="/trattamenti"
                      className="block px-4 py-2 text-sm text-foreground hover:bg-secondary hover:text-primary transition-colors font-semibold"
                      onClick={() => setIsTreatmentsOpen(false)}
                    >
                      Tutti i Trattamenti
                    </Link>
                    <div className="border-t border-border my-2" />
                    <p className="px-4 py-1 text-xs uppercase tracking-wide text-muted-foreground">
                      Trattamenti ayurvedici
                    </p>
                    {ayurvedici.map((treatment) => (
                      <Link
                        key={treatment.slug}
                        to={`/trattamenti/${treatment.slug}`}
                        className="block px-4 py-2 text-sm text-foreground hover:bg-secondary hover:text-primary transition-colors"
                        onClick={() => setIsTreatmentsOpen(false)}
                      >
                        {treatment.name}
                      </Link>
                    ))}
                    <div className="border-t border-border my-2" />
                    <p className="px-4 py-1 text-xs uppercase tracking-wide text-muted-foreground">
                      Altri trattamenti
                    </p>
                    {altriTrattamenti.map((treatment) => (
                      <Link
                        key={treatment.slug}
                        to={`/trattamenti/${treatment.slug}`}
                        className="block px-4 py-2 text-sm text-foreground hover:bg-secondary hover:text-primary transition-colors"
                        onClick={() => setIsTreatmentsOpen(false)}
                      >
                        {treatment.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => scrollToSection("contatti")}
              className="text-foreground hover:text-primary transition-colors font-medium"
            >
              Contatti
            </button>

            <a
              href="https://wa.me/393461533000"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 inline-flex items-center rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-5 py-2.5 text-sm font-medium transition-colors"
            >
              Prenota
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-foreground"
            aria-label={isOpen ? "Chiudi menu" : "Apri menu"}
            aria-expanded={isOpen}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden py-4 space-y-4 max-h-[80vh] overflow-y-auto">
            <button
              onClick={() => scrollToSection("home")}
              className="block w-full text-left text-foreground hover:text-primary transition-colors font-medium py-2"
            >
              Home
            </button>
            <button
              onClick={() => scrollToSection("studio")}
              className="block w-full text-left text-foreground hover:text-primary transition-colors font-medium py-2"
            >
              Lo Studio
            </button>
            
            <div className="space-y-2">
              <Link
                to="/trattamenti"
                className="block w-full text-left text-foreground hover:text-primary transition-colors font-bold py-2"
                onClick={() => setIsOpen(false)}
              >
                Trattamenti
              </Link>
              <div className="pl-4 space-y-2">
                {mobileQuickLinks.map((treatment) => (
                  <Link
                    key={treatment.slug}
                    to={`/trattamenti/${treatment.slug}`}
                    className="block w-full text-left text-muted-foreground hover:text-primary transition-colors text-sm py-1"
                    onClick={() => setIsOpen(false)}
                  >
                    {treatment.name}
                  </Link>
                ))}
                <Link
                  to="/trattamenti"
                  className="block w-full text-left text-primary hover:text-accent transition-colors text-sm font-semibold py-1"
                  onClick={() => setIsOpen(false)}
                >
                  Tutti i trattamenti →
                </Link>
              </div>
            </div>

            <button
              onClick={() => scrollToSection("contatti")}
              className="block w-full text-left text-foreground hover:text-primary transition-colors font-medium py-2"
            >
              Contatti
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
